import {
  GetContrastColorBW,
  useFeedback,
  useTranslation,
} from "@opendash/core";
import { _User } from "@opendash/plugin-parse/dist/types-generated";
import { ColorPicker } from "@opendash/ui";
import { Button, Form, Input, InputNumber, Modal } from "antd";
import Parse from "parse";
import React, { useContext, useState } from "react";
import { AgencyContext } from "../contexts/AgencyContext";
import { Route } from "../js/parsegenerated";
import { TourId } from "../types/TourId";
import { RouteTypeSelect } from "./RouteTypeSelect";
import Infolabel from "./UI/Infolabel";

export interface RouteDataType {
  route_id: string;
  route_short_name: string;
  route_long_name: string;
  route_desc: string;
  route_type: number;
  route_url: string;
  route_color: string;
  route_text_color: string;
  route_sort_order: number;
}

export const RouteCreateModal = (props: {
  open: boolean;
  onOk: Function;
  onCancel: Function;
}) => {
  const t = useTranslation();
  const { message } = useFeedback();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [color, setColor] = useState<string>("#FFFFFF");
  const [textColor, setTextColor] = useState<string>("#000000");
  const [valuesHaveChanged, setValuesHaveChanged] = useState(false);
  const { TextArea } = Input;
  const state = useContext(AgencyContext);

  const save = async () => {
    form.validateFields().then(async (values: RouteDataType) => {
      setLoading(true);
      const route: Route = new Route();

      const savedroute = await route.save({
        agency_id: state.selectedAgency,
        route_id: values.route_id ? values.route_id : crypto.randomUUID(),
        route_short_name: values.route_short_name,
        route_long_name: values.route_long_name,
        route_desc: values.route_desc,
        route_type: values.route_type,
        route_url: values.route_url,
        route_color: values.route_color,
        route_text_color: values.route_text_color,
        route_sort_order: values.route_sort_order,
        key:
          state.selectedAgency.id +
          "_" +
          values.route_id +
          "_" +
          values.route_short_name,
        tenant: (Parse.User.current() as _User).get("tenant"),
      });

      if (!values.route_id) {
        savedroute.set("route_id", savedroute.id);
        await savedroute.save();
      }
      message.success(t("gtfs:route.create.modal.form.success"));
      props.onOk(savedroute);
      form.resetFields();
      setLoading(false);
    });
  };

  return (
    <>
      <Modal
        title={t("gtfs:route.create.modal.title")}
        open={props.open}
        onOk={save}
        onCancel={() => {
          props.onCancel();
        }}
        width={"80%"}
        getContainer={false}
        footer={[
          <Button
            id={TourId.Route.close}
            key="back"
            onClick={() => {
              props.onCancel();
            }}
          >
            {t("gtfs:global.button.cancel_label")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={save}
            id={TourId.Route.save}
          >
            {t("gtfs:global.button.save_label")}
          </Button>,
        ]}
        modalRender={(modal) => <div id={TourId.Route.modal}>{modal}</div>}
      >
        <Form
          autoComplete="off"
          form={form}
          layout="vertical"
          onValuesChange={() => {
            setValuesHaveChanged(true);
          }}
        >
          <Form.Item
            name="route_id"
            label={t("gtfs:route.create.modal.form.id.label")}
            help={t("gtfs:route.create.modal.form.id.help")}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="route_short_name"
            label={
              <Infolabel
                title={t("gtfs:route.create.modal.form.short_name.label")}
                info={t("gtfs:route.create.modal.form.short_name.info")}
              />
            }
            help={t("gtfs:route.create.modal.form.short_name.help")}
            rules={[
              {
                required: true,
                message: t(
                  "gtfs:route.create.modal.form.short_name.rules.required.message"
                ),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="route_long_name"
            label={
              <Infolabel
                title={t("gtfs:route.create.modal.form.long_name.label")}
                info={t("gtfs:route.create.modal.form.long_name.info")}
              />
            }
            help={t("gtfs:route.create.modal.form.long_name.help")}
            rules={[
              {
                required: true,
                message: t(
                  "gtfs:route.create.modal.form.long_name.rules.required.message"
                ),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="route_desc"
            label={t("gtfs:route.create.modal.form.desc.label")}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item
            name="route_type"
            label={t("gtfs:route.create.modal.form.type.label")}
            rules={[
              {
                required: true,
                message: t(
                  "gtfs:route.create.modal.form.type.rules.required.message"
                ),
              },
            ]}
          >
            <RouteTypeSelect />
          </Form.Item>
          <Form.Item
            name="route_url"
            label={t("gtfs:route.create.modal.form.url.label")}
            help={t("gtfs:route.create.modal.form.url.help")}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="route_color"
            initialValue={color}
            label={
              <Infolabel
                title={t("gtfs:route.create.modal.form.color.label")}
                info={t("gtfs:route.create.modal.form.color.info")}
              />
            }
            rules={[
              {
                required: false,
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <ColorPicker
              value={color}
              onChange={(value) => {
                setColor(value);
              }}
            />
          </Form.Item>
          <Button
            onClick={() => setColor(GetContrastColorBW(textColor))}
            style={{ marginBottom: 24 }}
          >
            {t("gtfs:route.create.modal.form.text_color.auto_button")}
          </Button>
          <Form.Item
            name="route_text_color"
            initialValue={textColor}
            label={
              <Infolabel
                title={t("gtfs:route.create.modal.form.text_color.label")}
                info={t("gtfs:route.create.modal.form.text_color.info")}
              />
            }
            rules={[
              {
                required: false,
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <ColorPicker
              value={textColor}
              onChange={(value) => {
                setTextColor(value);
              }}
            />
          </Form.Item>
          <Button
            onClick={() => setTextColor(GetContrastColorBW(color))}
            style={{ marginBottom: 24 }}
          >
            {t("gtfs:route.create.modal.form.text_color.auto_button")}
          </Button>
          <Form.Item
            name="route_sort_order"
            label={
              <Infolabel
                title={t("gtfs:route.create.modal.form.sort_order.label")}
                info={t("gtfs:route.create.modal.form.sort_order.info")}
              />
            }
            help={t("gtfs:route.create.modal.form.sort_order.help")}
            rules={[
              {
                required: false,
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
